import { useModal as useFinalModal, useModalSlot } from 'vue-final-modal';
import type { Component } from 'vue';
import { ConfirmModalProps } from './interfaces';
import ModalLayout from './../modal/ModalLayout.vue';
import ConfirmModalTemplate from './../modal/ConfirmModalTemplate.vue';
import 'vue-final-modal/style.css';

export type ModalOptions = {
  modalProps?: any;
  dataTestid?: string;
  customClose?: () => any;
  layoutProps?: any;
  hideClose?: boolean;
  hideOverlay?: boolean;
  clickToClose?: boolean;
  onClickOutside?: () => void;
  noBorder?: boolean;
};

export const useModal = <P>(modalComponent: Component<P>, modalOptions?: ModalOptions) => {
  const { open, close, destroy, options, patchOptions } = useFinalModal({
    keepAlive: false,
    component: ModalLayout,
    attrs: {
      dataTestid: modalOptions?.dataTestid,
      onClose: () => (modalOptions?.customClose ? modalOptions.customClose() : close()),
      onClickOutside: modalOptions?.onClickOutside,
      hideClose: modalOptions?.hideClose,
      noBorder: modalOptions?.noBorder,
      clickToClose: modalOptions?.clickToClose,
      hideOverlay: modalOptions?.hideOverlay,
      ...modalOptions?.layoutProps,
    },
    slots: {
      default: useModalSlot({
        component: modalComponent,
        attrs: {
          onClose: () => (modalOptions?.customClose ? modalOptions.customClose() : close()),
          ...modalOptions?.modalProps,
        },
      }),
    },
  });

  return { open, close, destroy, options, patchOptions };
};

export const useConfirmModal = (modalProps: ConfirmModalProps) => {
  const onOpenConfirm = (): Promise<boolean> =>
    new Promise(resolve => {
      const { open, close } = useModal(ConfirmModalTemplate, {
        modalProps: {
          ...modalProps,
          onConfirm: (confirmed: boolean) => {
            resolve(confirmed);
            close();
          },
        },
        customClose: () => {
          resolve(false);
          close();
        },
      });

      open();
    });

  return { open: onOpenConfirm };
};
